import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ChevronRight,
  ChevronLeft,
  Star,
  Crown,
  Users,
  TrendingUp,
  Zap,
  Brain,
  Network,
  Bot,
  DollarSign,
  Gift,
  Plane,
  Ship,
  Mountain,
  Building2,
  Globe,
  Award,
  Shield,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Percent,
  Package,
  ArrowRight,
  Sparkles,
  Target,
  Cpu,
  Binary,
  CircuitBoard,
  Menu,
  X,
  Mail
} from "lucide-react";

// Logo URL
const LOGO_URL = "/achraj_logo.png";

// Plan Data
const PLANS = [
  { type: "A", price: 4000, levels: 20, color: "from-amber-400 to-orange-500", maxIncome: "₹4.19 Cr" },
  { type: "B", price: 2000, levels: 19, color: "from-purple-500 to-violet-600", maxIncome: "₹2.09 Cr" },
  { type: "C", price: 1000, levels: 18, color: "from-cyan-500 to-blue-600", maxIncome: "₹1.04 Cr" },
  { type: "D", price: 500, levels: 17, color: "from-slate-500 to-slate-600", maxIncome: "₹52.42 L" },
];

// Distribution Breakdown (for ₹1000 Plan C)
const DISTRIBUTION = [
  { label: "Product Cost (Factory Direct)", amount: 500, percent: 50, color: "emerald" },
  { label: "GST 5%", amount: 50, percent: 5, color: "amber" },
  { label: "Delivery Reserve", amount: 50, percent: 5, color: "blue" },
  { label: "20 Levels Distribution", amount: 400, percent: 40, color: "cyan", highlight: true },
];

// Star Journey - Levels 1-10
const STAR_LEVELS = [
  { level: 1, joining: 2, team: 2, income: 40, reward: null, icon: Star },
  { level: 2, joining: 4, team: 6, income: 120, reward: null, icon: Star },
  { level: 3, joining: 8, team: 14, income: 280, reward: null, icon: Star },
  { level: 4, joining: 16, team: 30, income: 600, reward: null, icon: Star },
  { level: 5, joining: 32, team: 62, income: 1240, reward: null, icon: Star },
  { level: 6, joining: 64, team: 126, income: 2520, reward: "Varanasi Trip", rewardIcon: "🕌", icon: Star },
  { level: 7, joining: 128, team: 254, income: 5080, reward: "Lucknow 5-Star", rewardIcon: "🏰", icon: Star },
  { level: 8, joining: 256, team: 510, income: 10200, reward: "Resort 2D/1N", rewardIcon: "🏨", icon: Star },
  { level: 9, joining: 512, team: 1022, income: 20440, reward: "Uttarakhand 3D", rewardIcon: "🏔", icon: Star },
  { level: 10, joining: 1024, team: 2046, income: 40920, reward: "Jaipur 4D", rewardIcon: "🕌", icon: Star },
];

// Leader Journey - Levels 11-20
const LEADER_LEVELS = [
  { level: 11, rank: "Branch", rankIcon: "🥉", ss: 4, team: 4094, income: 81880, reward: "Goa Trip", rewardIcon: "✈️", color: "orange" },
  { level: 12, rank: "Silver", rankIcon: "🥈", ss: 8, team: 8190, income: 163800, reward: "Cruise", rewardIcon: "🚢", color: "slate" },
  { level: 13, rank: "Gold", rankIcon: "🥇", ss: 12, team: 16382, income: 327640, reward: "Thailand", rewardIcon: "🇹🇭", color: "amber" },
  { level: 14, rank: "Platinum", rankIcon: "💎", ss: 16, team: 32766, income: 655320, reward: "Malaysia", rewardIcon: "🇲🇾", color: "cyan" },
  { level: 15, rank: "Ruby", rankIcon: "🔴", ss: 20, team: 65534, income: 1310680, reward: "Euro Cruise", rewardIcon: "🚢", color: "red" },
  { level: 16, rank: "Sapphire", rankIcon: "💙", ss: 24, team: 131070, income: 2621400, reward: "Africa", rewardIcon: "🌍", color: "blue" },
  { level: 17, rank: "Emerald", rankIcon: "💚", ss: 28, team: 262142, income: 5242840, reward: "Dubai", rewardIcon: "🇦🇪", color: "emerald" },
  { level: 18, rank: "Diamond", rankIcon: "💠", ss: 32, team: 524286, income: 10485720, reward: "Switzerland", rewardIcon: "🇨🇭", color: "purple" },
  { level: 19, rank: "Double Diamond", rankIcon: "💠💠", ss: 36, team: 1048574, income: 20971480, reward: "Australia", rewardIcon: "🇦🇺", color: "violet" },
  { level: 20, rank: "Crown Ambassador", rankIcon: "👑", ss: 40, team: 2097150, income: 41943000, reward: "Latin America", rewardIcon: "🌎", color: "gold" },
];

// Monthly Purchase Rules
const PURCHASE_RULES = [
  { months: 1, penalty: 25, status: "warning" },
  { months: 2, penalty: 50, status: "warning" },
  { months: 3, penalty: 75, status: "danger" },
  { months: 4, penalty: 90, status: "danger" },
  { months: 5, penalty: 100, status: "critical", note: "ID Replace" },
];

// Format currency
const formatCurrency = (amount) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
};

// Grid Background Component
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px]"></div>
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]"></div>
  </div>
);

export default function AILevelIncomePlan() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("C");

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white" data-testid="ai-income-plan-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0F1C]/80 backdrop-blur-xl border-b border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full blur opacity-50"></div>
                <img src={LOGO_URL} alt="Achraj Logo" className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover border-2 border-cyan-500/50" />
              </div>
              <div className="hidden sm:block">
                <span className="font-bold text-lg bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Achraj AI</span>
                <p className="text-xs text-slate-400">Income Plan</p>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium text-sm">Home</Link>
              <a href="#plans" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium text-sm">Plans</a>
              <a href="#star-journey" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium text-sm">Star Journey</a>
              <a href="#leader-journey" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium text-sm">Leader Journey</a>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white rounded-full px-6 shadow-lg shadow-cyan-500/25">
                  Join Now
                </Button>
              </Link>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6 text-cyan-400" /> : <Menu className="w-6 h-6 text-cyan-400" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0F1C]/95 backdrop-blur-xl border-t border-cyan-500/10 py-4 px-4">
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-slate-300 py-2 px-3 rounded-lg hover:bg-cyan-500/10">Home</Link>
              <a href="#plans" className="text-slate-300 py-2 px-3 rounded-lg hover:bg-cyan-500/10" onClick={() => setMobileMenuOpen(false)}>Plans</a>
              <a href="#star-journey" className="text-slate-300 py-2 px-3 rounded-lg hover:bg-cyan-500/10" onClick={() => setMobileMenuOpen(false)}>Star Journey</a>
              <a href="#leader-journey" className="text-slate-300 py-2 px-3 rounded-lg hover:bg-cyan-500/10" onClick={() => setMobileMenuOpen(false)}>Leader Journey</a>
              <Link to="/register"><Button className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full w-full mt-2">Join Now</Button></Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4 relative overflow-hidden">
        <GridBackground />
        
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded-full text-sm font-medium mb-6">
            <Brain className="w-4 h-4 animate-pulse" />
            <span>AI Level Income Plan</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">अचरज </span>
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">AI Income System</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-8">
            20 Level Binary Matrix with AI Auto-Fill Technology. 
            <span className="text-cyan-400 font-semibold"> सिर्फ 2 लोग जोड़ें</span>, बाकी AI करेगा!
          </p>

          {/* Company Info */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
              <Building2 className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-300">ACHRAJ SERVICES PVT. LTD.</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-300">CIN: U47912UP2026PTC244373</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
              <CheckCircle className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-slate-300">Govt. Registered</span>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans" className="py-12 sm:py-16 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4" /> Subscription Plans
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Choose Your AI Level</h2>
            <p className="text-slate-400">Higher plans = More AI Levels = Higher Income</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
            {PLANS.map((plan) => (
              <div 
                key={plan.type} 
                className={`relative group cursor-pointer ${selectedPlan === plan.type ? 'ring-2 ring-cyan-500' : ''}`}
                onClick={() => setSelectedPlan(plan.type)}
              >
                {plan.type === "C" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-xs font-bold rounded-full z-10">
                    POPULAR
                  </div>
                )}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-500"></div>
                <Card className="relative bg-slate-900/90 border border-slate-700/50 rounded-2xl overflow-hidden backdrop-blur-xl h-full">
                  <CardContent className="p-5 text-center">
                    <div className={`w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center shadow-lg`}>
                      <span className="text-xl font-bold text-white">{plan.type}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-1">₹{plan.price.toLocaleString()}</h3>
                    <p className="text-slate-400 text-sm mb-3">per month</p>
                    <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">AI Levels</span>
                        <span className="text-cyan-400 font-bold">{plan.levels}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Max Income</span>
                        <span className="text-amber-400 font-bold">{plan.maxIncome}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Distribution Breakdown */}
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">₹1,000 Plan Distribution (Plan C)</h3>
              <p className="text-slate-400 text-sm">See how your subscription is distributed</p>
            </div>
            
            <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6 backdrop-blur-xl">
              <div className="space-y-4">
                {DISTRIBUTION.map((item, idx) => (
                  <div key={idx} className="relative">
                    <div className="flex justify-between items-center mb-2">
                      <span className={`text-sm ${item.highlight ? 'text-cyan-400 font-semibold' : 'text-slate-300'}`}>
                        {item.label}
                      </span>
                      <span className={`font-bold ${item.highlight ? 'text-cyan-400' : 'text-white'}`}>
                        ₹{item.amount} ({item.percent}%)
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${item.highlight ? 'from-cyan-500 to-purple-500' : `from-${item.color}-500 to-${item.color}-400`} rounded-full transition-all duration-500`}
                        style={{ width: `${item.percent}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-700">
                <div className="flex items-center justify-center gap-3 text-center">
                  <Cpu className="w-5 h-5 text-cyan-400" />
                  <p className="text-slate-300">
                    <span className="text-cyan-400 font-bold">₹20 per Level</span> × 20 Levels = 
                    <span className="text-amber-400 font-bold"> 2% BV Income</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Binary System Explanation */}
      <section className="py-12 sm:py-16 px-4 relative">
        <GridBackground />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-full text-sm font-medium mb-4">
              <Bot className="w-4 h-4 animate-pulse" /> AI Binary System
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">How AI Builds Your Team</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">आप सिर्फ 2 लोग जोड़ें, बाकी सब AI Automatic करेगा!</p>
          </div>

          {/* AI Binary Tree Visualization */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative bg-slate-900/80 border border-cyan-500/20 rounded-2xl p-6 sm:p-8 backdrop-blur-xl">
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl blur-2xl -z-10"></div>
              
              <div className="flex flex-col items-center">
                {/* You */}
                <div className="relative mb-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/30 border-2 border-cyan-400">
                    <span className="text-white font-bold">YOU</span>
                  </div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-black text-[10px] font-bold rounded-full">
                    START
                  </div>
                </div>

                {/* Level 1 */}
                <div className="w-px h-6 bg-gradient-to-b from-cyan-500 to-cyan-500/50"></div>
                <div className="w-40 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>
                <div className="flex justify-between w-40">
                  <div className="w-px h-6 bg-cyan-500/50"></div>
                  <div className="w-px h-6 bg-cyan-500/50"></div>
                </div>
                
                <div className="flex justify-between w-40 mb-2">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-slate-800 border border-cyan-500/50 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="text-xs text-slate-400 mt-1">Direct 1</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-slate-800 border border-purple-500/50 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-xs text-slate-400 mt-1">Direct 2</span>
                  </div>
                </div>

                {/* AI fills rest */}
                <div className="flex items-center gap-2 mt-4 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <Binary className="w-4 h-4 text-cyan-400 animate-pulse" />
                  <span className="text-sm text-cyan-400">AI fills Level 2 to 20 automatically from global registrations</span>
                </div>

                {/* Level 10 Result */}
                <div className="mt-6 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
                  <p className="text-center">
                    <span className="text-slate-300">Level 10 पर </span>
                    <span className="text-3xl font-bold text-amber-400">1,024</span>
                    <span className="text-slate-300"> members Automatic!</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Points */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { icon: Users, title: "Just Add 2 People", desc: "सिर्फ 2 Direct joining करें", color: "cyan" },
              { icon: Bot, title: "AI Does The Rest", desc: "AI Binary tree fill करता है", color: "purple" },
              { icon: TrendingUp, title: "Earn From All Levels", desc: "सभी 20 levels से income", color: "emerald" },
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
                <div className={`w-12 h-12 mx-auto mb-3 bg-${item.color}-500/20 rounded-xl flex items-center justify-center`}>
                  <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <h4 className="font-bold text-white mb-1">{item.title}</h4>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Star Journey - Levels 1-10 */}
      <section id="star-journey" className="py-12 sm:py-16 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4" /> Star Journey
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Level 1 to 10</h2>
            <p className="text-slate-400">Auto-unlock with just 2 Direct Sponsors. Monthly income + Travel rewards!</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="py-4 px-4 text-left text-slate-400 font-medium">Level</th>
                  <th className="py-4 px-4 text-center text-slate-400 font-medium">AI Team Size</th>
                  <th className="py-4 px-4 text-center text-slate-400 font-medium">Total Team</th>
                  <th className="py-4 px-4 text-center text-slate-400 font-medium">Monthly Income</th>
                  <th className="py-4 px-4 text-center text-slate-400 font-medium">Reward</th>
                </tr>
              </thead>
              <tbody>
                {STAR_LEVELS.map((level, idx) => (
                  <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold">{level.level}</span>
                        </div>
                        <span className="text-white font-medium">Level {level.level}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-cyan-400 font-bold">{level.joining.toLocaleString()}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-purple-400 font-bold">{level.team.toLocaleString()}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-emerald-400 font-bold text-lg">₹{level.income.toLocaleString()}</span>
                      <span className="text-slate-500 text-sm">/month</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      {level.reward ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full">
                          <span className="text-lg">{level.rewardIcon}</span>
                          <span className="text-amber-400 text-sm font-medium">{level.reward}</span>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl text-center">
            <p className="text-cyan-400">
              <CheckCircle className="w-5 h-5 inline mr-2" />
              <span className="font-bold">Level 1-10 Auto-Unlock</span> with just 2 Direct Sponsors!
            </p>
          </div>
        </div>
      </section>

      {/* Leader Journey - Levels 11-20 */}
      <section id="leader-journey" className="py-12 sm:py-16 px-4 relative">
        <GridBackground />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded-full text-sm font-medium mb-4">
              <Crown className="w-4 h-4" /> Leader Journey
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Level 11 to 20</h2>
            <p className="text-slate-400">Unlock higher levels with Self Sponsors. International trips + Massive income!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {LEADER_LEVELS.map((level, idx) => (
              <div key={idx} className="group relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-500"></div>
                <Card className="relative bg-slate-900/80 border border-slate-700/50 rounded-2xl overflow-hidden backdrop-blur-xl">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg`}>
                          <span className="text-white font-bold">{level.level}</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{level.rankIcon}</span>
                            <span className="text-white font-bold">{level.rank}</span>
                          </div>
                          <p className="text-slate-400 text-sm">Level {level.level}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-400">{formatCurrency(level.income)}</p>
                        <p className="text-slate-500 text-xs">Monthly Income</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <p className="text-cyan-400 font-bold">{level.ss}</p>
                        <p className="text-slate-400 text-xs">Self Sponsors</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <p className="text-purple-400 font-bold">{level.team.toLocaleString()}</p>
                        <p className="text-slate-400 text-xs">Team Size</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-lg">{level.rewardIcon}</span>
                          <span className="text-amber-400 text-xs font-medium">{level.reward}</span>
                        </div>
                        <p className="text-slate-400 text-xs">Reward Trip</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                      <div className="flex items-center gap-2 text-sm">
                        <Target className="w-4 h-4 text-cyan-400" />
                        <span className="text-slate-400">Required: <span className="text-cyan-400 font-bold">{level.ss} Self Sponsors</span></span>
                      </div>
                      <Plane className="w-5 h-5 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl text-center">
            <p className="text-purple-400">
              <Award className="w-5 h-5 inline mr-2" />
              <span className="font-bold">SS = Self Sponsor</span> - Additional direct joinings required to unlock each leader level
            </p>
          </div>
        </div>
      </section>

      {/* Policy Section */}
      <section className="py-12 sm:py-16 px-4 relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-500/10 border border-slate-500/30 text-slate-400 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" /> Policy & Rules
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Important Policies</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Policies */}
            <Card className="bg-slate-900/80 border border-slate-700/50 rounded-2xl backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  General Policies
                </h3>
                <ul className="space-y-3">
                  {[
                    { icon: Calendar, text: "Month End Closing → Payout 1-5 तारीख" },
                    { icon: Percent, text: "10% Service Charge on income" },
                    { icon: Package, text: "7 Day Product Return Policy" },
                    { icon: Users, text: "Nominee Transfer सुविधा available" },
                    { icon: DollarSign, text: "Monthly Purchase अनिवार्य" },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-300">
                      <item.icon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Monthly Purchase Rules */}
            <Card className="bg-slate-900/80 border border-red-500/20 rounded-2xl backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Monthly Purchase Rules
                </h3>
                <div className="space-y-3">
                  {PURCHASE_RULES.map((rule, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        rule.status === 'critical' 
                          ? 'bg-red-500/10 border border-red-500/30' 
                          : rule.status === 'danger'
                          ? 'bg-orange-500/10 border border-orange-500/30'
                          : 'bg-amber-500/10 border border-amber-500/30'
                      }`}
                    >
                      <span className="text-slate-300 text-sm">
                        {rule.months} Month{rule.months > 1 ? 's' : ''} Miss
                      </span>
                      <span className={`font-bold ${
                        rule.status === 'critical' ? 'text-red-400' : 
                        rule.status === 'danger' ? 'text-orange-400' : 'text-amber-400'
                      }`}>
                        {rule.note || `${rule.penalty}% कटौती`}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10"></div>
        <GridBackground />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            <span className="text-white">Ready to </span>
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">Start Your AI Journey?</span>
          </h2>
          
          <p className="text-slate-400 text-lg mb-8">
            ❝ जुड़कर खर्च नहीं, <span className="text-cyan-400 font-bold">आय करें</span> ❞
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/register">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl shadow-cyan-500/25">
                <Sparkles className="mr-2 w-5 h-5" /> Join Achraj AI Now
              </Button>
            </Link>
            <a href="mailto:achrajaigroup@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors">
              <Mail className="w-5 h-5" /> achrajaigroup@gmail.com
            </a>
          </div>
          
          <p className="mt-8 text-slate-500 text-sm">🙏 जय Achraj परिवार 🙏</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-[#050810] border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs text-slate-500 mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-cyan-500" />
              <span>Achraj Services Private Limited</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-purple-500" />
              <span className="font-mono">CIN: U47912UP2026PTC244373</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-emerald-500" />
              <span>www.achrajai.com</span>
            </div>
          </div>
          <p className="text-xs text-slate-600">© 2026 Achraj Services Private Limited. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
